import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useToast } from '../../hooks/use-toast';
import { Save, Edit, X, Upload, Plus, Trash2, History } from 'lucide-react';
import api from '../../utils/api';

const CompanyInfoManagement = () => {
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    tagline: '',
    address: '',
    phone: '',
    fax: '',
    email: '',
    business_hours: '',
    logo_url: '',
    representative_director: '',
    established: '',
    philosophy_title: '',
    philosophy_description: '',
    values: [],
    history: [],
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchCompanyInfo();
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      const response = await api.get('/company-info');
      setFormData({
        name: response.data.name,
        tagline: response.data.tagline,
        address: response.data.address,
        phone: response.data.phone,
        fax: response.data.fax,
        email: response.data.email,
        business_hours: response.data.business_hours,
        logo_url: response.data.logo_url || '',
        representative_director: response.data.representative_director || '',
        established: response.data.established || '',
        philosophy_title: response.data.philosophy_title || '',
        philosophy_description: response.data.philosophy_description || '',
        values: Array.isArray(response.data.values) ? response.data.values : [],
        history: Array.isArray(response.data.history) ? response.data.history : [],
      });
      setLogoPreview(response.data.logo_url || null);
    } catch (error) {
      console.error('会社概要の取得に失敗しました:', error);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // ファイルサイズチェック（最大2MB）
      if (file.size > 2 * 1024 * 1024) {
        toast({
          title: 'エラー',
          description: 'ファイルサイズは2MB以下にしてください',
          variant: 'destructive',
        });
        return;
      }

      // 画像タイプチェック
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'エラー',
          description: '画像ファイルを選択してください',
          variant: 'destructive',
        });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setLogoPreview(base64String);
        setFormData({ ...formData, logo_url: base64String });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const parseYearMonth = (s) => {
      const m = String(s || '').match(/(\d+)\s*年(?:\s*(\d+)\s*月)?/);
      if (!m) return Number.MAX_SAFE_INTEGER;
      return parseInt(m[1], 10) * 100 + (m[2] ? parseInt(m[2], 10) : 0);
    };
    const sortedHistory = [...formData.history].sort((a, b) => parseYearMonth(a.date) - parseYearMonth(b.date));
    const payload = { ...formData, history: sortedHistory };

    try {
      await api.put('/company-info', payload);
      toast({
        title: '更新完了',
        description: '会社概要を更新しました',
      });
      setIsEditing(false);
      fetchCompanyInfo();
    } catch (error) {
      toast({
        title: 'エラー',
        description: error.response?.data?.detail || '更新に失敗しました',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    fetchCompanyInfo();
  };

  const handleValueChange = (index, field, val) => {
    const newValues = formData.values.map((v, i) =>
      i === index ? { ...v, [field]: val } : v
    );
    setFormData({ ...formData, values: newValues });
  };

  const handleHistoryChange = (index, field, val) => {
    const newHistory = formData.history.map((h, i) =>
      i === index ? { ...h, [field]: val } : h
    );
    setFormData({ ...formData, history: newHistory });
  };

  const handleAddHistory = () => {
    setFormData({ ...formData, history: [...formData.history, { date: '', text: '' }] });
  };

  const handleRemoveHistory = (index) => {
    setFormData({ ...formData, history: formData.history.filter((_, i) => i !== index) });
  };

  return (
    <Card className="bg-gradient-to-br from-gray-50/80 to-gray-100/80 border-amber-500/30 backdrop-blur-sm">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <CardTitle className="text-xl sm:text-2xl bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
            会社概要管理
          </CardTitle>
          {!isEditing ? (
            <Button 
              onClick={() => setIsEditing(true)}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 shadow-lg shadow-amber-500/30"
            >
              <Edit className="w-4 h-4 mr-2" />
              編集
            </Button>
          ) : (
            <Button 
              onClick={handleCancel}
              variant="outline"
              className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10"
            >
              <X className="w-4 h-4 mr-2" />
              キャンセル
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* ロゴマーク */}
          <div className="space-y-4">
            <Label className="text-gray-600">ロゴマーク</Label>
            <div className="flex items-center space-x-4">
              {logoPreview && (
                <div className="w-32 h-32 border-2 border-amber-500/30 rounded-lg overflow-hidden bg-white/10 flex items-center justify-center">
                  <img 
                    src={logoPreview} 
                    alt="Company Logo" 
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              )}
              {isEditing && (
                <div className="flex-1">
                  <Label 
                    htmlFor="logo-upload" 
                    className="cursor-pointer inline-flex items-center px-4 py-2 bg-white/50 border border-amber-500/30 rounded-lg hover:bg-amber-500/10 transition-all"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    ロゴをアップロード
                  </Label>
                  <Input
                    id="logo-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="hidden"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    推奨: PNG、JPG形式、最大2MB
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-gray-600">会社名</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
                disabled={!isEditing}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tagline" className="text-gray-600">キャッチフレーズ</Label>
              <Input
                id="tagline"
                value={formData.tagline}
                onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
                disabled={!isEditing}
                required
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="representative_director" className="text-gray-600">代表取締役</Label>
              <Input
                id="representative_director"
                value={formData.representative_director}
                onChange={(e) => setFormData({ ...formData, representative_director: e.target.value })}
                className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
                disabled={!isEditing}
                placeholder="例: 黒崎 龍一"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="established" className="text-gray-600">設立</Label>
              <Input
                id="established"
                value={formData.established}
                onChange={(e) => setFormData({ ...formData, established: e.target.value })}
                className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
                disabled={!isEditing}
                placeholder="例: 2024年"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="address" className="text-gray-600">住所</Label>
            <Input
              id="address"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
              disabled={!isEditing}
              required
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-gray-600">電話番号</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
                disabled={!isEditing}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fax" className="text-gray-600">FAX</Label>
              <Input
                id="fax"
                value={formData.fax}
                onChange={(e) => setFormData({ ...formData, fax: e.target.value })}
                className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
                disabled={!isEditing}
                required
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-600">メールアドレス</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
                disabled={!isEditing}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="business_hours" className="text-gray-600">営業時間</Label>
              <Input
                id="business_hours"
                value={formData.business_hours}
                onChange={(e) => setFormData({ ...formData, business_hours: e.target.value })}
                className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
                disabled={!isEditing}
                required
              />
            </div>
          </div>

          {/* 企業理念セクション（会社概要ページに表示） */}
          <div className="space-y-4 border-t border-amber-500/20 pt-6">
            <h3 className="text-lg font-semibold text-amber-500">企業理念（会社概要ページ）</h3>
            <div className="space-y-2">
              <Label htmlFor="philosophy_title" className="text-gray-600">企業理念・キャッチコピー</Label>
              <Input
                id="philosophy_title"
                data-testid="company-philosophy-title-input"
                value={formData.philosophy_title}
                onChange={(e) => setFormData({ ...formData, philosophy_title: e.target.value })}
                className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
                disabled={!isEditing}
                placeholder="例: より良いこれからを創造する会社です。"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="philosophy_description" className="text-gray-600">企業理念・説明文</Label>
              <Textarea
                id="philosophy_description"
                data-testid="company-philosophy-description-input"
                value={formData.philosophy_description}
                onChange={(e) => setFormData({ ...formData, philosophy_description: e.target.value })}
                className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500 min-h-[100px]"
                disabled={!isEditing}
                placeholder="企業理念の説明文を入力してください"
              />
            </div>
          </div>

          {/* 私たちの価値観セクション */}
          <div className="space-y-4 border-t border-amber-500/20 pt-6">
            <h3 className="text-lg font-semibold text-amber-500">私たちの価値観（会社概要ページ）</h3>
            {formData.values.map((value, index) => (
              <div key={index} className="grid md:grid-cols-3 gap-4 items-start bg-white/30 rounded-lg p-4 border border-amber-500/10">
                <div className="space-y-2">
                  <Label className="text-gray-600">項目 {index + 1}・タイトル</Label>
                  <Input
                    data-testid={`company-value-title-${index}`}
                    value={value.title}
                    onChange={(e) => handleValueChange(index, 'title', e.target.value)}
                    className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
                    disabled={!isEditing}
                    placeholder="例: 尊重"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label className="text-gray-600">説明文</Label>
                  <Input
                    data-testid={`company-value-description-${index}`}
                    value={value.description}
                    onChange={(e) => handleValueChange(index, 'description', e.target.value)}
                    className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
                    disabled={!isEditing}
                    placeholder="説明文を入力してください"
                  />
                </div>
              </div>
            ))}
          </div>

          {/* 沿革セクション */}
          <div className="space-y-4 border-t border-amber-500/20 pt-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-amber-500 flex items-center">
                <History className="w-5 h-5 mr-2" />
                沿革（会社概要ページ）
              </h3>
              {isEditing && (
                <Button
                  type="button"
                  size="sm"
                  onClick={handleAddHistory}
                  data-testid="company-history-add-button"
                  className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  項目を追加
                </Button>
              )}
            </div>
            {formData.history.length === 0 && (
              <p className="text-sm text-gray-500">沿革の項目がありません。「項目を追加」から追加してください。</p>
            )}
            <p className="text-xs text-gray-500">保存時に年月の古い順へ自動で並び替わります（例: 2002年7月 → 2026年）。</p>
            {formData.history.map((item, index) => (
              <div key={index} className="grid md:grid-cols-[160px_1fr_auto] gap-4 items-start bg-white/30 rounded-lg p-4 border border-amber-500/10" data-testid={`company-history-row-${index}`}>
                <div className="space-y-2">
                  <Label className="text-gray-600">年月</Label>
                  <Input
                    data-testid={`company-history-date-${index}`}
                    value={item.date}
                    onChange={(e) => handleHistoryChange(index, 'date', e.target.value)}
                    className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
                    disabled={!isEditing}
                    placeholder="例: 2002年7月"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-600">内容</Label>
                  <Textarea
                    data-testid={`company-history-text-${index}`}
                    value={item.text}
                    onChange={(e) => handleHistoryChange(index, 'text', e.target.value)}
                    className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500 min-h-[60px]"
                    disabled={!isEditing}
                    placeholder="例: 地域生活支援事業を開始"
                  />
                </div>
                {isEditing && (
                  <div className="flex md:flex-col gap-2 md:pt-6">
                    <Button type="button" size="icon" variant="outline" onClick={() => handleRemoveHistory(index)} data-testid={`company-history-remove-${index}`} className="border-red-400/40 text-red-500 hover:bg-red-500/10 h-8 w-8">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {isEditing && (
            <div className="flex justify-end">
              <Button 
                type="submit"
                data-testid="company-info-save-button"
                className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 shadow-lg shadow-amber-500/30"
                disabled={loading}
              >
                <Save className="w-4 h-4 mr-2" />
                {loading ? '保存中...' : '保存'}
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
};

export default CompanyInfoManagement;
